import type { ErrorCopy } from '@shared/errors/catalog';

/**
 * The codes only the account detail meets, across the three services it reads.
 *
 * **Never inline a backend code in JSX.** The code is a log key; the sentence is
 * what reaches the operator, and the `X-Request-ID` is what they quote.
 *
 * Two entries carry more than a translation:
 *
 * `AUTH_BIOGRAPHY_NOT_APPLICABLE` is a **409, not a 403** — a conditional field
 * refusing a write rather than a permission refusal. It is reachable in one real
 * race: the account's role was revoked by another operator between this page's
 * load and the save, so the section is still on screen for a value that no
 * longer exists. The copy names that, because «راجع مسؤولاً» would send the
 * operator to ask for a permission they already hold.
 *
 * `FINANCE_ALREADY_ENTITLED` is a refusal to layer one grant over another, and
 * the reason it is a refusal rather than an extension is that two overlapping
 * catalogue entitlements make «when does access end» unanswerable. The copy says
 * the account already has what the grant would have given it.
 */
export const errors: Readonly<Record<string, ErrorCopy>> = {
  // ---- backend-auth ------------------------------------------------------
  AUTH_ROLE_REQUIRED: {
    message: 'تغيير الأدوار والحظر ومنح الاستحقاق من صلاحية المدير. راجع مسؤولاً لإجرائه.',
    tone: 'warning',
  },
  AUTH_NOT_FOUND: {
    message: 'لم نعثر على هذا الحساب. قد يكون حُذف بينما كانت الصفحة مفتوحة.',
    tone: 'warning',
  },
  AUTH_ROLE_UNKNOWN: {
    message: 'هذا الدور غير معروف في هذه الخدمة. أعد تحميل الصفحة ثمّ اختر من القائمة.',
    tone: 'warning',
  },
  AUTH_BIOGRAPHY_NOT_APPLICABLE: {
    message: 'لم يعد هذا الحساب يحمل دوراً، فلا نبذة له. أعد تحميل الصفحة لقراءة حالته الحالية.',
    tone: 'warning',
  },

  // ---- backend-finance ---------------------------------------------------
  FINANCE_FORBIDDEN: {
    message: 'منح الاستحقاق من صلاحية المدير. راجع مسؤولاً لإجرائه.',
    tone: 'warning',
  },
  FINANCE_ALREADY_ENTITLED: {
    message: 'هذا الحساب يملك وصولاً فاعلاً بالفعل. انتظر انتهاءه قبل منح استحقاق آخر.',
    tone: 'warning',
  },
  FINANCE_ENTITLEMENT_MODE_NOT_PERMITTED: {
    message: 'المنحة اليدوية غير مسموحة في التهيئة الحالية. راجع إعدادات الاستحقاق.',
    tone: 'warning',
  },
  FINANCE_NOT_FOUND: {
    message: 'لم نعثر على استحقاقات هذا الحساب.',
    tone: 'warning',
  },
  FINANCE_UPSTREAM_TIMEOUT: {
    message: 'تأخّرت خدمة الاشتراك عن الردّ. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },

  // ---- backend-support ---------------------------------------------------
  SUPPORT_SCOPE_REQUIRED: {
    message: 'قراءة سجلّ الحالات تحتاج دوراً أوسع. راجع مسؤولاً.',
    tone: 'warning',
  },
  SUPPORT_FILTER_COLUMN_NOT_ALLOWED: {
    message: 'تعذّر قراءة سجلّ الحالات لهذا الحساب. أعد تحميل الصفحة.',
    tone: 'warning',
  },
};
